import type { ReactNode } from 'react';
import { ensureProtocol, isArchiProURL, isExternalURL } from '~/modules/url';
import type { Participant } from '../types';
import {
    MENTION_TOKEN_REGEX,
    getMentionChipClassName,
    getUnknownMentionChipClassName,
    isSelfMention,
    resolveMentionToken,
    type MentionLookup,
} from './mentions';

/**
 * Regular expression to match valid website URLs in text.
 * Matches http, https protocols and URLs starting with www.
 * Supports domains, ports, paths, query parameters, and fragments.
 * Excludes trailing periods and commas after the URL.
 */
const URL_REGEX =
    /(?:\b|^)(?:https?:\/\/|www\.)(?:localhost|[a-z0-9]+(?:[.-][a-z0-9]+)*\.[a-z]{2,})(?::[0-9]{1,5})?(?:\/[a-z0-9._~%-]*[a-z0-9_~%+]+)*\/?(?:[?#](?:[-a-z0-9._~%=&+:@/?!$'*,;#]*[-a-z0-9_~%=&+:@/?!$'*;#])?)?(?=\s|\b|$|[.,](?:\s|$))/gi;

/**
 * Information about an ArchiPro directory item.
 */
interface DirectoryItemInfo {
    /**
     * The original URL.
     */
    originalUrl: string;
    /**
     * The absolute URL for the directory item.
     */
    href: string;
    /**
     * The path to the directory item.
     */
    pathname: string;
    /**
     * The directory item type.
     */
    type: 'product' | 'project' | 'professional' | 'article';
}

type ParticipantIdentity = Pick<Participant['Identity'], '__typename' | 'ID'>;

/**
 * Extract website URLs from plain text content.
 */
export function extractUrlsFromText(text: string): string[] {
    const urls: string[] = [];
    URL_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_REGEX.exec(text)) !== null) {
        urls.push(match[0]);
    }
    return urls;
}

/**
 * Extract unique website URLs from plain text content while preserving order.
 */
export function extractUniqueUrlsFromText(text: string): string[] {
    return Array.from(new Set(extractUrlsFromText(text)));
}

/**
 * Parse ArchiPro URLs for directory items and return information about the item.
 * @param link The URL to parse.
 * @returns Information about the directory item if it is a valid ArchiPro URL.
 */
export function parseArchiProItemLink(link: string): DirectoryItemInfo | null {
    const fullUrl = ensureProtocol(link);
    if (!isArchiProURL(fullUrl)) {
        return null;
    }

    let url: URL;
    try {
        const baseUrl =
            typeof window !== 'undefined'
                ? window.location.origin
                : 'https://archipro.co.nz';
        url = new URL(fullUrl, link.startsWith('/') ? baseUrl : undefined);
    } catch {
        return null;
    }

    const [prefix, slug] = url.pathname.split('/').filter(Boolean);
    if (!prefix || !slug) {
        return null;
    }
    if (
        prefix !== 'product' &&
        prefix !== 'project' &&
        prefix !== 'professional' &&
        prefix !== 'article'
    ) {
        return null;
    }

    const linkPath = `/${prefix}/${slug}`;
    return {
        originalUrl: link,
        href: `${url.origin}${linkPath}`,
        pathname: linkPath,
        type: prefix,
    };
}

/**
 * Extract all URLs that support chat message link previews.
 * This includes ArchiPro item links and valid external links.
 */
export function extractChatPreviewUrls(text: string): string[] {
    const links = extractUniqueUrlsFromText(text);
    return links
        .map((link) => {
            const normalizedHref = ensureProtocol(link);
            if (
                !isArchiProURL(normalizedHref) &&
                !isExternalURL(normalizedHref)
            ) {
                return null;
            }
            return normalizedHref;
        })
        .filter((href): href is string => !!href);
}

/**
 * Converts URLs in plain text to clickable links.
 * Returns an array of React nodes with text and link elements.
 * @param textOrNodes The plain text or react nodes to linkify.
 * @param linkClassName The class name to apply to the links.
 * @returns Array of React nodes with text and links.
 * @example
 * linkifyText('Check out https://example.com')
 * // Returns: ['Check out ', <a href="https://example.com" ...>https://example.com</a>]
 */
export function linkifyText(
    textOrNodes: ReactNode | ReactNode[],
    linkClassName?: string,
): ReactNode[] {
    if (Array.isArray(textOrNodes)) {
        return textOrNodes.flatMap((node, index) => {
            return linkifyTextNode(node, linkClassName, `node-${index}`);
        });
    }
    return linkifyTextNode(textOrNodes, linkClassName, 'node-0');
}

/**
 * Linkify only string leaves and leave existing React nodes untouched.
 */
function linkifyTextNode(
    node: ReactNode,
    linkClassName: string | undefined,
    keyPrefix: string,
): ReactNode[] {
    if (typeof node === 'string') {
        return linkifyString(node, linkClassName, keyPrefix);
    }
    if (Array.isArray(node)) {
        return node.flatMap((child, index) => {
            return linkifyTextNode(
                child,
                linkClassName,
                `${keyPrefix}-${index}`,
            );
        });
    }
    return [node];
}

/**
 * Split a string into plain text and `<a>` nodes for each matched URL.
 */
function linkifyString(
    text: string,
    linkClassName?: string,
    keyPrefix: string = 'link',
): ReactNode[] {
    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Reset regex state
    URL_REGEX.lastIndex = 0;

    while ((match = URL_REGEX.exec(text)) !== null) {
        const url = match[0];
        const index = match.index;

        // Add text before the URL
        if (index > lastIndex) {
            parts.push(text.slice(lastIndex, index));
        }

        // Determine the href - add https:// protocol if missing (for www. URLs)
        const href = ensureProtocol(url);

        // Add the link element
        parts.push(
            <a
                key={`${keyPrefix}-link-${index}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClassName}
            >
                {url}
            </a>,
        );

        lastIndex = index + url.length;
    }

    // Add remaining text after the last URL
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    // If no URLs were found, return the original text
    return parts.length > 0 ? parts : [text];
}

/**
 * Linkify URLs and render token mentions in plain text. Mention tokens use the
 * format `<@U{id}>` or `<@P{id}>`.
 * @returns An array of React nodes with text, links, and mention spans.
 */
export function linkifyTextWithMentions({
    text,
    mentionLookup = {},
    selfIdentity,
    linkClassName,
    renderUnknownMentions = false,
}: {
    /**
     * The text to render.
     */
    text: string;
    /**
     * The mention lookup map.
     */
    mentionLookup?: MentionLookup;
    /**
     * The identity of the self participant in the conversation.
     */
    selfIdentity?: ParticipantIdentity | null;
    /**
     * The class name to apply to the links.
     */
    linkClassName?: string;
    /**
     * Render unknown mentions as `@Unknown`.
     */
    renderUnknownMentions?: boolean;
}): ReactNode[] {
    const textAndMentionNodes = renderTextWithMentionTokens({
        text,
        mentionLookup,
        selfIdentity,
        renderUnknownMentions,
        keyPrefix: 'mention',
    });
    return linkifyText(textAndMentionNodes, linkClassName);
}

/**
 * Render mention tokens as spans with the display names of the participants.
 * Mention tokens use the format `<@U{id}>` or `<@P{id}>`.
 * @returns An array of React nodes with text and mention spans.
 */
function renderTextWithMentionTokens({
    text,
    mentionLookup,
    selfIdentity,
    renderUnknownMentions,
    keyPrefix,
}: {
    /**
     * The text to render.
     */
    text: string;
    /**
     * The mention lookup map.
     */
    mentionLookup: MentionLookup;
    /**
     * The identity of the self participant in the conversation.
     */
    selfIdentity?: ParticipantIdentity | null;
    /**
     * Render unknown mentions as `@Unknown`.
     */
    renderUnknownMentions: boolean;
    /**
     * The prefix to use for the React key.
     */
    keyPrefix: string;
}): ReactNode[] {
    const parts: ReactNode[] = [];
    let tokenMatch: RegExpExecArray | null;
    let lastIndex = 0;

    MENTION_TOKEN_REGEX.lastIndex = 0;
    while ((tokenMatch = MENTION_TOKEN_REGEX.exec(text)) !== null) {
        const rawToken = tokenMatch[0];
        const matchIndex = tokenMatch.index;
        if (matchIndex > lastIndex) {
            parts.push(text.slice(lastIndex, matchIndex));
        }
        const mention = resolveMentionToken({
            rawToken,
            mentionLookup,
        });
        if (mention) {
            parts.push(
                <span
                    key={`${keyPrefix}-${matchIndex}`}
                    className={getMentionChipClassName(
                        isSelfMention({
                            mention,
                            userID:
                                selfIdentity?.__typename === 'Member'
                                    ? selfIdentity.ID
                                    : null,
                            professionalID:
                                selfIdentity?.__typename === 'Professional'
                                    ? selfIdentity.ID
                                    : null,
                        }),
                    )}
                >
                    @{mention.displayName}
                </span>,
            );
        } else {
            if (renderUnknownMentions) {
                parts.push(
                    <span
                        key={`${keyPrefix}-${matchIndex}`}
                        className={getUnknownMentionChipClassName()}
                    >
                        @Unknown
                    </span>,
                );
            } else {
                parts.push(rawToken);
            }
        }
        lastIndex = matchIndex + rawToken.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : [text];
}
